import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  ListTodo, Plus, RefreshCw, Trash2, Pencil, Paperclip, Download, Filter,
  CheckCircle2, Clock, AlertOctagon, CircleDot, Ban, Gauge,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { API, api, endpoints, getTenantScope } from "@/lib/api";
import { toast } from "sonner";

const fmt = (s) => (s ? new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");

const STATUS_TONE = {
  "Open": "bg-blue-100 text-blue-800 border-blue-200",
  "In Progress": "bg-amber-100 text-amber-800 border-amber-200",
  "Blocked": "bg-rose-100 text-rose-800 border-rose-200",
  "Completed": "bg-emerald-100 text-emerald-800 border-emerald-200",
};
const PRIORITY_TONE = {
  "Low": "bg-slate-100 text-slate-700",
  "Medium": "bg-sky-100 text-sky-700",
  "High": "bg-orange-100 text-orange-700",
  "Critical": "bg-rose-100 text-rose-700",
};
const STATUS_ICON = {
  "Open": CircleDot, "In Progress": Clock, "Blocked": Ban, "Completed": CheckCircle2,
};

export default function ClientIsoTasksPage() {
  const scope = getTenantScope();
  const [params] = useSearchParams();
  const qp = params.get("client");
  const [clientSlug, setClientSlug] = useState(qp || scope?.slug || "northwind-corp");
  const [clients, setClients] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [dash, setDash] = useState(null);
  const [meta, setMeta] = useState({ statuses: [], priorities: [], categories: [] });
  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [modal, setModal] = useState({ open: false, edit: null });
  const isClient = ["client", "client_admin", "client_employee"].includes(scope?.role);
  const readOnly = scope?.role === "auditor";

  const load = () => {
    endpoints.listClients().then(setClients).catch(() => {});
    endpoints.isoTasksMetadata().then(setMeta).catch(() => {});
    endpoints.listIsoTasks({ client_slug: clientSlug }).then(setTasks).catch(() => setTasks([]));
    endpoints.isoTasksDashboard(clientSlug).then(setDash).catch(() => setDash(null));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientSlug]);

  const filtered = useMemo(() => {
    return tasks.filter(t =>
      (statusFilter === "all" || t.status === statusFilter) &&
      (priorityFilter === "all" || t.priority === priorityFilter)
    );
  }, [tasks, statusFilter, priorityFilter]);

  const del = async (id) => {
    if (!window.confirm("Delete this task? All evidence attachments will be removed.")) return;
    try { await endpoints.deleteIsoTask(id); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-6 lg:p-8 max-w-6xl mx-auto">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-3">
          <span className="h-10 w-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 text-white flex items-center justify-center shadow-md">
            <ListTodo className="h-5 w-5" />
          </span>
          <div>
            <h1 className="font-display text-2xl font-extrabold tracking-tight">Client ISO Task List</h1>
            <p className="text-xs text-slate-500">Track assignments, evidence and progress on ISO compliance actions.</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {!isClient && (
            <Select value={clientSlug} onValueChange={setClientSlug}>
              <SelectTrigger className="w-48" data-testid="iso-tasks-client-select"><SelectValue /></SelectTrigger>
              <SelectContent>{clients.map(c => <SelectItem key={c.slug} value={c.slug}>{c.company_name}</SelectItem>)}</SelectContent>
            </Select>
          )}
          <Button variant="outline" size="sm" onClick={load} data-testid="iso-tasks-refresh"><RefreshCw className="h-4 w-4 mr-1" /> Refresh</Button>
          {!readOnly && (
            <Button className="brand-gradient text-white" size="sm" onClick={() => setModal({ open: true, edit: null })} data-testid="iso-tasks-add">
              <Plus className="h-4 w-4 mr-1" /> New Task
            </Button>
          )}
        </div>
      </div>

      {/* Dashboard */}
      {dash && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
          <StatCard label="Total tasks" value={dash.total} tone="indigo" testid="iso-stat-total" />
          <StatCard label="Overdue" value={dash.overdue} tone="rose" testid="iso-stat-overdue" />
          <StatCard label="Due this week" value={dash.due_soon} tone="amber" testid="iso-stat-due" />
          <StatCard label="Avg progress" value={`${dash.avg_progress}%`} tone="cyan" testid="iso-stat-progress" />
          <StatCard label="Completion" value={`${dash.completion_rate}%`} tone="emerald" testid="iso-stat-completion" />
        </div>
      )}

      {/* Status breakdown pills */}
      {dash && (
        <Card className="glass-card mb-4"><CardContent className="p-4">
          <div className="flex items-center gap-4 flex-wrap">
            <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Status</span>
            {Object.entries(dash.by_status).map(([s, c]) => (
              <button key={s} onClick={() => setStatusFilter(s)} className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs ${statusFilter === s ? "border-indigo-400 bg-indigo-50" : "border-slate-200 hover:bg-slate-50"}`}>
                <span className={`inline-block h-2 w-2 rounded-full ${STATUS_TONE[s]?.split(" ")[0] || "bg-slate-300"}`} />
                {s} <span className="font-bold">{c}</span>
              </button>
            ))}
            {statusFilter !== "all" && <Button variant="ghost" size="sm" onClick={() => setStatusFilter("all")}>Clear</Button>}
          </div>
        </CardContent></Card>
      )}

      {/* Filters */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <Filter className="h-4 w-4 text-slate-400" />
        <Select value={priorityFilter} onValueChange={setPriorityFilter}>
          <SelectTrigger className="w-36" data-testid="iso-tasks-filter-priority"><SelectValue placeholder="Priority" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All priorities</SelectItem>
            {meta.priorities.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
          </SelectContent>
        </Select>
        <span className="text-xs text-slate-500">{filtered.length} of {tasks.length} tasks</span>
      </div>

      {/* Task list */}
      {filtered.length === 0 ? (
        <div className="text-center py-12 border border-dashed rounded-2xl bg-slate-50/50">
          <ListTodo className="h-8 w-8 mx-auto text-slate-300 mb-2" />
          <div className="text-sm text-slate-500 mb-3">No tasks match the current filters.</div>
          {!readOnly && <Button onClick={() => setModal({ open: true, edit: null })} className="brand-gradient text-white"><Plus className="h-4 w-4 mr-1" /> Create first task</Button>}
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(t => (
            <TaskCard key={t.id} task={t} readOnly={readOnly}
              onEdit={() => setModal({ open: true, edit: t })}
              onDelete={() => del(t.id)}
              onReload={load} />
          ))}
        </div>
      )}

      <TaskModal open={modal.open} edit={modal.edit} clientSlug={clientSlug} meta={meta}
        onClose={() => setModal({ open: false, edit: null })}
        onSaved={() => { setModal({ open: false, edit: null }); load(); }} />
    </div>
  );
}

function StatCard({ label, value, tone, testid }) {
  const g = {
    indigo: "from-indigo-500 to-purple-600", rose: "from-rose-500 to-orange-500",
    amber: "from-amber-500 to-orange-500", cyan: "from-cyan-500 to-teal-500",
    emerald: "from-emerald-500 to-teal-500",
  }[tone] || "from-indigo-500 to-purple-600";
  return (
    <Card className="glass-card overflow-hidden" data-testid={testid}>
      <div className={`h-1 bg-gradient-to-r ${g}`} />
      <CardContent className="p-3">
        <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{label}</div>
        <div className="text-2xl font-extrabold font-display mt-0.5">{value}</div>
      </CardContent>
    </Card>
  );
}

function TaskCard({ task, readOnly, onEdit, onDelete, onReload }) {
  const StatusIcon = STATUS_ICON[task.status] || CircleDot;
  const [expanded, setExpanded] = useState(false);
  const uploadEvidence = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      await api.post(`/iso-tasks/${task.id}/evidence`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Evidence uploaded"); onReload();
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    e.target.value = "";
  };
  const delEv = async (evId) => {
    if (!window.confirm("Delete this attachment?")) return;
    try { await api.delete(`/iso-tasks/${task.id}/evidence/${evId}`); toast.success("Removed"); onReload(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  return (
    <Card className="glass-card" data-testid={`iso-task-${task.id}`}>
      <CardContent className="p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <StatusIcon className="h-4 w-4 text-slate-400 shrink-0" />
              <div className="font-semibold truncate">{task.title}</div>
              <Badge variant="outline" className={`text-[10px] rounded-full ${STATUS_TONE[task.status]}`}>{task.status}</Badge>
              <Badge className={`text-[10px] rounded-full ${PRIORITY_TONE[task.priority]}`}>{task.priority}</Badge>
              {task.overdue && <Badge className="bg-rose-600 text-white text-[10px] rounded-full"><AlertOctagon className="h-2.5 w-2.5 mr-1" />Overdue</Badge>}
              {task.due_soon && !task.overdue && <Badge className="bg-amber-500 text-white text-[10px] rounded-full">Due soon</Badge>}
              {(task.evidence?.length || 0) > 0 && <Badge variant="outline" className="text-[10px] rounded-full"><Paperclip className="h-2.5 w-2.5 mr-1" />{task.evidence.length}</Badge>}
            </div>
            <div className="text-xs text-slate-500 mt-1">
              {task.assignee || "Unassigned"} · Due {fmt(task.due_date)} · {task.category}{task.iso_standard ? ` · ${task.iso_standard}` : ""}
            </div>
            {task.description && <div className="text-xs text-slate-600 dark:text-slate-300 mt-2 line-clamp-2">{task.description}</div>}
            <div className="mt-3 flex items-center gap-2">
              <div className="flex-1 max-w-xs">
                <Progress value={task.progress || 0} className="h-2" />
              </div>
              <span className="text-[11px] font-mono text-slate-500 tabular-nums">{task.progress || 0}%</span>
            </div>
          </div>
          <div className="flex gap-1 shrink-0">
            <Button size="sm" variant="ghost" onClick={() => setExpanded(e => !e)} data-testid={`iso-task-expand-${task.id}`}>
              {expanded ? "Hide" : "Details"}
            </Button>
            {!readOnly && (
              <>
                <Button size="sm" variant="ghost" onClick={onEdit} data-testid={`iso-task-edit-${task.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                <Button size="sm" variant="ghost" onClick={onDelete} data-testid={`iso-task-delete-${task.id}`}><Trash2 className="h-3.5 w-3.5 text-rose-500" /></Button>
              </>
            )}
          </div>
        </div>
        {expanded && (
          <div className="mt-4 pt-4 border-t border-slate-200 dark:border-white/10 space-y-3">
            {task.description && <div><Label className="text-[10px] uppercase tracking-widest text-slate-500">Description</Label><div className="text-sm mt-1 whitespace-pre-wrap">{task.description}</div></div>}
            {task.completion_notes && <div><Label className="text-[10px] uppercase tracking-widest text-slate-500">Completion notes</Label><div className="text-sm mt-1 whitespace-pre-wrap">{task.completion_notes}</div></div>}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-[10px] uppercase tracking-widest text-slate-500">Evidence attachments</Label>
                {!readOnly && (
                  <label className="cursor-pointer">
                    <input type="file" className="hidden" onChange={uploadEvidence} data-testid={`iso-task-upload-${task.id}`} />
                    <span className="inline-flex items-center gap-1 rounded-md border border-indigo-200 bg-indigo-50 px-2 py-1 text-xs text-indigo-700 hover:bg-indigo-100"><Paperclip className="h-3 w-3" /> Upload</span>
                  </label>
                )}
              </div>
              {(task.evidence || []).length === 0 ? (
                <div className="text-xs text-slate-500 italic">No evidence attached yet.</div>
              ) : (
                <ul className="space-y-1">
                  {task.evidence.map(ev => (
                    <li key={ev.id} className="flex items-center justify-between rounded-md border border-slate-200 bg-white/60 px-3 py-1.5 text-xs">
                      <div className="flex items-center gap-2 min-w-0">
                        <Paperclip className="h-3 w-3 text-slate-400" />
                        <a href={`${API}/iso-tasks/${task.id}/evidence/${ev.id}`} target="_blank" rel="noreferrer" className="font-mono truncate text-indigo-600 hover:underline">{ev.filename}</a>
                        <span className="text-slate-400">({(ev.size / 1024).toFixed(0)} KB · {ev.uploaded_by})</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <a href={`${API}/iso-tasks/${task.id}/evidence/${ev.id}`} download={ev.filename} className="text-slate-500 hover:text-indigo-600"><Download className="h-3 w-3" /></a>
                        {!readOnly && <button onClick={() => delEv(ev.id)} className="text-rose-500 hover:text-rose-700"><Trash2 className="h-3 w-3" /></button>}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function TaskModal({ open, edit, clientSlug, meta, onClose, onSaved }) {
  const empty = {
    title: "", description: "", iso_standard: "", category: "General",
    assignee: "", assignee_email: "", priority: "Medium", status: "Open",
    progress: 0, due_date: "", completion_notes: "",
  };
  const [f, setF] = useState(empty);
  useEffect(() => { if (open) setF(edit ? { ...empty, ...edit } : empty); /* eslint-disable-next-line */ }, [open, edit]);

  const submit = async () => {
    if (!f.title.trim()) { toast.error("Title required"); return; }
    try {
      if (edit?.id) {
        const { id, evidence, client_slug, created_at, updated_at, created_by, overdue, due_soon, completed_at, ...updates } = f;
        await endpoints.updateIsoTask(edit.id, updates);
        toast.success("Task updated");
      } else {
        const payload = { ...f, client_slug: clientSlug };
        // strip completion_notes on create (backend doesn't accept it in Create)
        delete payload.completion_notes;
        await endpoints.createIsoTask(payload);
        toast.success("Task created");
      }
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (!open) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="iso-task-modal">
        <DialogHeader><DialogTitle>{edit ? "Edit ISO Task" : "New ISO Task"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Field label="Title *"><Input value={f.title} onChange={e => setF({ ...f, title: e.target.value })} data-testid="iso-task-title" placeholder="e.g. Review ISO 9001 clause 8.5" /></Field>
          <Field label="Description"><Textarea rows={2} value={f.description} onChange={e => setF({ ...f, description: e.target.value })} data-testid="iso-task-description" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Category">
              <Select value={f.category} onValueChange={v => setF({ ...f, category: v })}>
                <SelectTrigger data-testid="iso-task-category"><SelectValue /></SelectTrigger>
                <SelectContent>{meta.categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="ISO standard"><Input value={f.iso_standard} onChange={e => setF({ ...f, iso_standard: e.target.value })} placeholder="e.g. ISO 9001:2015" /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Assignee"><Input value={f.assignee} onChange={e => setF({ ...f, assignee: e.target.value })} data-testid="iso-task-assignee" placeholder="e.g. Kate Alcott" /></Field>
            <Field label="Assignee email"><Input type="email" value={f.assignee_email} onChange={e => setF({ ...f, assignee_email: e.target.value })} /></Field>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Priority">
              <Select value={f.priority} onValueChange={v => setF({ ...f, priority: v })}>
                <SelectTrigger data-testid="iso-task-priority"><SelectValue /></SelectTrigger>
                <SelectContent>{meta.priorities.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Status">
              <Select value={f.status} onValueChange={v => setF({ ...f, status: v })}>
                <SelectTrigger data-testid="iso-task-status"><SelectValue /></SelectTrigger>
                <SelectContent>{meta.statuses.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Due date"><Input type="date" value={f.due_date || ""} onChange={e => setF({ ...f, due_date: e.target.value })} data-testid="iso-task-due-date" /></Field>
          </div>
          <Field label={`Progress: ${f.progress}%`}>
            <input type="range" min="0" max="100" step="5" value={f.progress} onChange={e => setF({ ...f, progress: Number(e.target.value) })} className="w-full accent-indigo-600" data-testid="iso-task-progress" />
          </Field>
          {edit && (
            <Field label="Completion / status notes">
              <Textarea rows={2} value={f.completion_notes || ""} onChange={e => setF({ ...f, completion_notes: e.target.value })} placeholder="Add notes when marking complete or blocked" />
            </Field>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="brand-gradient text-white" onClick={submit} data-testid="iso-task-save">{edit ? "Save" : "Create"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) { return <div><Label className="text-xs">{label}</Label>{children}</div>; }
